import type { SourceType, ViewMode } from './types';

export type CapabilityId =
  'notifications' | 'messages' | 'tasks' | 'code' | 'mail' | 'agents' | 'workspace';
export type CapabilityRoute = 'radar' | CapabilityId;

export interface CapabilitySource {
  capability: Exclude<CapabilityId, 'notifications' | 'workspace'>;
  provider: SourceType;
}

const CAPABILITY_TO_VIEW: Record<CapabilityId, ViewMode> = {
  notifications: 'notifications',
  messages: 'slack',
  tasks: 'jira',
  code: 'github',
  mail: 'gmail',
  agents: 'agents',
  workspace: 'wiki',
};

const VIEW_TO_CAPABILITY: Record<Exclude<ViewMode, 'radar'>, CapabilityId> = {
  notifications: 'notifications',
  jira: 'tasks',
  slack: 'messages',
  github: 'code',
  gmail: 'mail',
  agents: 'agents',
  wiki: 'workspace',
};

const SOURCE_TO_CAPABILITY: Record<SourceType, CapabilitySource['capability']> = {
  jira: 'tasks',
  slack: 'messages',
  github: 'code',
  gmail: 'mail',
  agents: 'agents',
};

export function capabilityForView(view: Exclude<ViewMode, 'radar'>): CapabilityId {
  return VIEW_TO_CAPABILITY[view];
}

export function viewForCapability(capability: CapabilityId): ViewMode {
  return CAPABILITY_TO_VIEW[capability];
}

export function capabilityForSource(provider: SourceType): CapabilitySource {
  return { capability: SOURCE_TO_CAPABILITY[provider], provider };
}

export function routeForView(view: ViewMode): CapabilityRoute {
  return view === 'radar' ? 'radar' : capabilityForView(view);
}

function isCapabilityId(candidate: string): candidate is CapabilityId {
  switch (candidate) {
    case 'messages':
    case 'notifications':
    case 'tasks':
    case 'code':
    case 'mail':
    case 'agents':
    case 'workspace':
      return true;
    default:
      return false;
  }
}

export function parseCapabilityRoute(candidate: string | null): {
  view: ViewMode;
  legacy: boolean;
} {
  if (!candidate || candidate === 'radar') return { view: 'radar', legacy: false };
  if (isCapabilityId(candidate)) {
    return { view: viewForCapability(candidate), legacy: false };
  }
  if (
    candidate === 'jira' ||
    candidate === 'slack' ||
    candidate === 'github' ||
    candidate === 'gmail'
  ) {
    return { view: candidate, legacy: true };
  }
  return { view: 'radar', legacy: false };
}
